import { Overlay } from '@angular/cdk/overlay';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { IconType } from '@ng-icons/core';
import { CardDesignComponent } from 'src/app/@shared/components/design/slide-up-card/card-design.component';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import {
  MANAGE_ROUTE_ALERT,
  ROUTE_TRACKING,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { Section } from 'src/app/modules/route-planner/interfaces/route-planning/section.interface';
import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { SourceDestinationResultsMapComponent } from '../source-destination-results-map/source-destination-results-map.component';
import { Direction } from '@angular/cdk/bidi';
import { Action } from 'src/app/modules/route-planner/interfaces/route-planning/action.interface';
import { SourceDestinationRouteTrackingActionComponent } from './source-destination-route-tracking-action/source-destination-route-tracking-action.component';
import { BoardingDebugState, RouteTrackingService } from 'src/app/modules/route-planner/services/route-tracking/route-tracking.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { BoardBusConfirmationPopupComponent } from './board-bus-confirmation-popup/board-bus-confirmation-popup.component';
import { Subscription, take } from 'rxjs';
import { Router } from '@angular/router';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { eSectionType } from 'src/app/modules/route-planner/enums/route-planning/section-type.enum';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-source-destination-route-tracking',
    templateUrl: './source-destination-route-tracking.component.html',
    styleUrl: './source-destination-route-tracking.component.scss',
    standalone: false
})
export class SourceDestinationRouteTrackingComponent
  extends CardDesignComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(SourceDestinationResultsMapComponent)
  mapComponent: SourceDestinationResultsMapComponent;

  @ViewChildren(SourceDestinationRouteTrackingActionComponent)
  actionComponents: QueryList<SourceDestinationRouteTrackingActionComponent>;

  mapService: MapService; // We define it here and not using DI because each map component use new instance of map service
  currentTrackingInstructions: (
    | Section
    | { section: Section; activeAction: Action }
  )[] = [];
  swipeHandler: SwipeHandler = new SwipeHandler(
    this.languageService.getSelectedLanguage().direction
  );

  currentSection: number = 0;
  currentAction: number = 0;
  
  // Track bus boarding state
  isUserOnBus: boolean = false;
  boardedBusSectionIndex: number | null = null;
  
  // Track last distance to prevent unnecessary updates
  private lastUpdateDistance: number = -1;
  private readonly MIN_DISTANCE_CHANGE_FOR_UPDATE = 10; // 10 meters minimum change to update
  
  // Track if user manually changed action (via swipe) - prevent auto-update from reverting
  private lastManualActionChange: number = 0;
  private readonly MANUAL_CHANGE_COOLDOWN_MS = 5000; // 5 seconds cooldown after manual change

  // Track last valid instruction
  private lastValidInstruction: { section: Section; activeAction: Action } =
    null;

  /** HERE map `tap` listener cleanup (avoid stacking handlers on re-init). */
  private mapTapCleanup: (() => void) | null = null;

  displayInstructions: boolean = true;

  trackingRouteText: string;
  trackingRouteAlert: string;

  icon: IconType;
  
  // Subscriptions
  private trackingSubscriptions: Subscription[] = [];
  /** From `environment.routePlannerDebugUi` — toggles boarding debug overlay for this module. */
  readonly routePlannerDebugUi = environment.routePlannerDebugUi;
  debugOverlayExpanded: boolean = true;
  boardingDebugState: BoardingDebugState | null = null;

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,
    private readonly languageService: LanguageService,
    private readonly routeTrackingService: RouteTrackingService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly userLocationService: UserLocationService,
    @Inject(DATA_INJECTION_TOKEN)
    public readonly trackedRoute: RoutePlanning
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  /**
   * Transit section index for map vehicle API (`getNextRouteLocation`).
   * Must match the leg the user is boarding / on — not always the first TRANSIT in the plan.
   */
  get vehiclePollTransitSectionIndex(): number | null {
    const sections = this.trackedRoute?.sections;
    if (!sections?.length) {
      return null;
    }
    if (this.isUserOnBus && this.boardedBusSectionIndex != null) {
      const idx = this.boardedBusSectionIndex;
      return sections[idx]?.type === eSectionType.TRANSIT ? idx : null;
    }
    // `currentSection` can flicker (e.g. TRANSIT vs PEDESTRIAN at transfer). Without skipping legs
    // whose scheduled arrival has passed, the first TRANSIT after `currentSection` alternates
    // between the bus you left and the one you wait for — marker and polyline jump.
    const fromCurrent = this.resolveVehiclePollTransitSectionIndex(
      sections,
      this.currentSection
    );
    if (fromCurrent != null) {
      return fromCurrent;
    }
    return this.resolveVehiclePollTransitSectionIndex(sections, 0);
  }

  /**
   * First TRANSIT at or after {@link startIndex} whose scheduled arrival is not already in the past
   * (leg effectively finished). Missing {@link Section.arrival.time} is not skipped.
   */
  private resolveVehiclePollTransitSectionIndex(
    sections: Section[],
    startIndex: number
  ): number | null {
    const now = Date.now();
    for (let i = Math.max(0, startIndex); i < sections.length; i++) {
      if (sections[i]?.type !== eSectionType.TRANSIT) {
        continue;
      }
      const arr = sections[i].arrival?.time;
      if (arr != null) {
        const arrT = arr instanceof Date ? arr : new Date(arr as string | number);
        if (now > arrT.getTime()) {
          continue;
        }
      }
      return i;
    }
    return null;
  }
  
  override ngOnDestroy(): void {
    this.mapTapCleanup?.();
    this.mapTapCleanup = null;
    this.routePlanningService.stopTrackingRoute();
    this.trackingSubscriptions.forEach((sub) => sub.unsubscribe());
    this.trackingSubscriptions = [];
    super.ngOnDestroy();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    const languageSub = this.languageService.selectedLanguage$.subscribe((lang) => {
      this.trackingRouteText = ROUTE_TRACKING[lang.language];
      this.trackingRouteAlert = MANAGE_ROUTE_ALERT[lang.language];
      this.icon = lang.direction === 'rtl' ? 'chevron-right' : 'chevron-left';
    });
    this.trackingSubscriptions.push(languageSub);
    
    // Subscribe to automatic section switching
    const sectionChangeSub = this.routeTrackingService.sectionChange$.subscribe(
      ({ sectionIndex, actionIndex }) => {
        this.currentSection = sectionIndex;
        if (actionIndex !== undefined) {
          this.currentAction = actionIndex;
        }
        // Reset distance tracking when section changes
        this.lastUpdateDistance = -1;
        this.changeTrackingStage(0); // Update display
      }
    );
    this.trackingSubscriptions.push(sectionChangeSub);
    
    // Subscribe to location updates for automatic pedestrian section instruction updates
    const locationSub = this.userLocationService.$userLocationChanged.subscribe(
      ({ location }) => {
        if (!location) return;
        
        const currentSection = this.trackedRoute.sections[this.currentSection];
        if (!currentSection || currentSection.type !== eSectionType.PEDESTRIAN) {
          return; // Only for pedestrian sections
        }
        
        // Automatically update instructions based on location
        this.updatePedestrianInstructions(location, currentSection);
      }
    );
    this.trackingSubscriptions.push(locationSub);
    
    // Subscribe to bus boarding popup display
    const boardBusPopupSub = this.routeTrackingService.showBoardBusPopup$.subscribe(
      ({ section }) => {
        this.showBoardBusConfirmationPopup(section);
      }
    );
    this.trackingSubscriptions.push(boardBusPopupSub);
    
    // Subscribe to tracking state changes
    // NOTE: Only update section, not action - action is managed by automatic updates and user swipes
    const trackingStateSub = this.routeTrackingService.trackingState$.subscribe(
      (state) => {
        // Update bus boarding state
        this.isUserOnBus = state.isUserOnBus;
        this.boardedBusSectionIndex = state.boardedBusSectionIndex;
        
        // Synchronize section with UI, but don't override action if section hasn't changed
        // Action updates are handled by updatePedestrianInstructions and user swipes
        if (state.currentSectionIndex !== this.currentSection) {
          this.currentSection = state.currentSectionIndex;
          this.currentAction = state.currentActionIndex;
          this.changeTrackingStage(0);
        }
        // Don't update currentAction if section hasn't changed - let automatic updates handle it
      }
    );
    this.trackingSubscriptions.push(trackingStateSub);

    if (this.routePlannerDebugUi) {
      const boardingDebugSub = this.routeTrackingService.boardingDebugState$.subscribe(
        (state) => {
          this.boardingDebugState = state;
        }
      );
      this.trackingSubscriptions.push(boardingDebugSub);
    }
  }
  
  // Show bus boarding confirmation popup
  private showBoardBusConfirmationPopup(section: Section): void {
    const sectionIndex = this.trackedRoute.sections.findIndex(s => s === section);
    if (sectionIndex === -1) return;
    
    this.modalService
      .openPopUp(BoardBusConfirmationPopupComponent, {
        hasBackdrop: true,
        disableClose: true,
        panelClass: 'board-bus-confirmation-panel',
        data: { section, sectionIndex },
      })
      .subscribe((result: boolean | undefined) => {
        if (result === true) {
          // SR-101.3.1.2.1: User confirmed boarding
          this.routeTrackingService.confirmBoardingBus(sectionIndex);
          this.mapComponent?.requestImmediateVehicleLocation(sectionIndex);
          // SR-101.3.1.2.2: Start showing next bus (if available)
          this.showNextBusIfAvailable(sectionIndex);
        } else if (result === false) {
          // SR-101.3.1.1: User clicked "לא" - redirect to search page
          this.redirectToRoutePlanner();
        }
      });
  }
  
  // Show next bus on map (SR-101.3.1.2.2)
  private showNextBusIfAvailable(currentSectionIndex: number): void {
    // Find next TRANSIT section
    for (let i = currentSectionIndex + 1; i < this.trackedRoute.sections.length; i++) {
      if (this.trackedRoute.sections[i].type === eSectionType.TRANSIT) {
        break;
      }
    }
  }
  
  // Redirect to route search page (SR-101.3.1.1)
  private redirectToRoutePlanner(): void {
    // Close navigation screen
    this.closeCard();
    
    // Redirect to search page with original parameters
    // TODO: Save original search parameters and restore them
    this.router.navigate(['/route-planner'], {
      queryParams: {
        tab: eTab.SOURCE_DESTINATION
      }
    });
  }

  ngAfterViewInit(): void {
    this.mapService = this.mapComponent.mapService;
    this.mapService.setUiLayout(H.ui.LayoutAlignment.LEFT_TOP);

    const onMapTap = () => {
      this.displayInstructions = !this.displayInstructions;
    };

    const mapInitSub = this.mapService.$mapInitialized
      .pipe(take(1))
      .subscribe(() => {
        const map = this.mapService.map;
        if (map) {
          this.mapTapCleanup?.();
          map.addEventListener('tap', onMapTap);
          this.mapTapCleanup = () => map.removeEventListener('tap', onMapTap);
        }
        this.changeTrackingStage(0);
      });
    this.trackingSubscriptions.push(mapInitSub);

    const busMarkerSub = this.routeTrackingService.latestBusLocation$.subscribe(
      (coord) => {
        this.mapComponent?.showVehicleAt(coord ?? null);
      }
    );
    this.trackingSubscriptions.push(busMarkerSub);
  }

  changeTrackingStage(offset: number) {
    if (offset !== 0) {
      // Mark that user manually changed action
      this.lastManualActionChange = Date.now();
      // Notify action components about the swipe
      this.actionComponents.forEach((actionComponent) => {
        actionComponent.onSwipeDetected();
      });
    }

    const sections = this.trackedRoute.sections;

    if (!!sections[this.currentSection]?.actions.length) {
      this.changeTrackingAction(sections[this.currentSection], offset);
    } else {
      this.changeTrackingSection(sections, offset);
    }
  }

  private changeTrackingAction(section: Section, offset: number) {
    const stage = this.currentAction + offset;

    // Check if we're trying to go beyond the last instruction
    if (stage >= section.actions.length) {
      // Instead of transitioning to the next section, we'll just keep the last instruction
      if (this.lastValidInstruction && offset > 0) {
        this.currentTrackingInstructions = [this.lastValidInstruction];
        return;
      }

      this.changeTrackingSection(this.trackedRoute.sections, offset);
      return;
    } else if (stage < 0) {
      this.changeTrackingSection(this.trackedRoute.sections, offset);
      return;
    }

    this.currentAction = stage;
    this.currentTrackingInstructions = [];
    this.mapService.lookAtData([
      section.departure.place.location,
      section.arrival.place.location,
    ]);

    const actionItems = section.actions
      .slice(stage, stage + 2)
      .map((action) => ({ section: section, activeAction: action }));

    // Store the last valid instruction
    if (actionItems.length > 0 && actionItems[0].activeAction.instruction) {
      this.lastValidInstruction = actionItems[0];
    }

    this.currentTrackingInstructions.push(...actionItems);
    this.currentAction = stage;
  }

  private changeTrackingSection(sections: Section[], offset: number) {
    const stage = this.currentSection + offset;

    // If trying to go beyond the last section, keep the last valid instruction
    if (stage >= this.trackedRoute.sections.length) {
      if (this.lastValidInstruction && offset > 0) {
        this.currentTrackingInstructions = [this.lastValidInstruction];
        return;
      }
      return;
    } else if (stage < 0) {
      return;
    }

    this.currentTrackingInstructions = [];
    this.currentSection = stage;

    if (!sections[stage].actions?.length) {
      this.currentTrackingInstructions.push(
        ...sections.slice(stage, stage + 2)
      );
      this.mapService.lookAtData([
        sections[stage].departure.place.location,
        sections[stage].arrival.place.location,
      ]);
    } else {
      this.currentAction =
        offset > 0 ? 0 : sections[this.currentSection].actions?.length - 1;
      this.changeTrackingAction(sections[stage], 0);
    }
  }

  /** True when the item is a pedestrian step `{ section, activeAction }`, not a bare `Section`. */
  isPedestrianActionInstruction(
    instruction: Section | { section: Section; activeAction: Action }
  ): boolean {
    return (
      !!instruction &&
      typeof instruction === 'object' &&
      'activeAction' in instruction &&
      (instruction as { section: Section; activeAction: Action }).activeAction != null
    );
  }

  getSection(
    instruction: Section | { section: Section; activeAction: Action }
  ): Section {
    return instruction as Section;
  }

  /** Route `sections` index for this instruction card (boarding vs alight per leg). */
  getInstructionSectionRouteIndex(
    instruction: Section | { section: Section; activeAction: Action }
  ): number {
    const section = this.getSection(instruction);
    return this.trackedRoute.sections.findIndex((s) => s === section);
  }

  getAction(
    instruction: Section | { section: Section; activeAction: Action }
  ): { section: Section; activeAction: Action } {
    return instruction as { section: Section; activeAction: Action };
  }
  
  // Automatic instruction updates for pedestrian sections based on location
  private updatePedestrianInstructions(location: any, section: Section): void {
    if (!section.actions || section.actions.length === 0) {
      return;
    }
    
    // Calculate distance from section start to current location
    if (!section.departure?.place?.location) {
      return;
    }
    
    const distanceFromStart = this.calculateDistance(
      section.departure.place.location.lat,
      section.departure.place.location.lng,
      location.lat,
      location.lng
    );
    
    // Prevent too frequent updates - only update if distance changed significantly
    if (this.lastUpdateDistance >= 0) {
      const distanceChange = Math.abs(distanceFromStart - this.lastUpdateDistance);
      if (distanceChange < this.MIN_DISTANCE_CHANGE_FOR_UPDATE) {
        return; // Skip update if distance hasn't changed enough
      }
    }
    this.lastUpdateDistance = distanceFromStart;
    
    // If user recently manually changed action (via swipe), be more conservative
    const timeSinceManualChange = Date.now() - this.lastManualActionChange;
    const isRecentManualChange = timeSinceManualChange < this.MANUAL_CHANGE_COOLDOWN_MS;
    
    // Find instruction that matches current distance
    // Use offset (distance from section start) to determine current instruction
    // We need to find the instruction that the user should be seeing based on their progress
    let targetActionIndex = 0;
    
    // Find the last instruction where user has passed the offset point
    // Actions are typically ordered by offset (increasing)
    for (let i = section.actions.length - 1; i >= 0; i--) {
      const action = section.actions[i];
      // offset is the distance from section start to this instruction
      if (action.offset != null && distanceFromStart >= action.offset) {
        targetActionIndex = i;
        break; // Found the most appropriate instruction
      }
    }
    
    // If user has passed all instructions, show the last one
    if (distanceFromStart >= (section.actions[section.actions.length - 1]?.offset || 0)) {
      targetActionIndex = section.actions.length - 1;
    }
    
    // Calculate how far ahead/behind user is from current instruction
    const currentActionOffset = section.actions[this.currentAction]?.offset || 0;
    const targetActionOffset = section.actions[targetActionIndex]?.offset || 0;
    const distanceAhead = distanceFromStart - currentActionOffset;
    const skippedInstructions = targetActionIndex - this.currentAction;
    
    // Special case: User significantly ahead (likely returned to route after going off)
    // If user is 100+ meters ahead or skipped 3+ instructions, force update
    const isSignificantlyAhead = distanceAhead >= 100 || skippedInstructions >= 3;
    
    // Only update if we found a more advanced instruction (forward progress)
    // This prevents reverting to earlier instructions if GPS jumps backward
    if (targetActionIndex > this.currentAction && targetActionIndex < section.actions.length) {
      // If user recently manually changed, only update if significantly ahead
      if (isRecentManualChange && !isSignificantlyAhead) {
        // Only update if user is at least 30m ahead of current instruction
        if (distanceFromStart < currentActionOffset + 30) {
          return;
        }
      }
      
      // Force update if user significantly ahead (returned to route after going off)
      this.currentAction = targetActionIndex;
      this.changeTrackingAction(section, 0); // Update display
    } else if (targetActionIndex < this.currentAction && targetActionIndex >= 0) {
      // If user recently manually changed, don't revert backward
      if (isRecentManualChange) {
        return;
      }
      
      // Allow backward updates only if user is significantly behind (more than 50m)
      // This handles cases where GPS was inaccurate and user actually moved forward
      // OR user went off route and returned at an earlier point
      const distanceBehind = currentActionOffset - distanceFromStart;
      if (distanceBehind > 50) {
        this.currentAction = targetActionIndex;
        this.changeTrackingAction(section, 0);
      }
    }
  }
  
  // Calculate distance between two points (Haversine formula)
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  formatDebugNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return value.toFixed(1);
  }

  formatDebugTimestamp(value: string | null | undefined): string {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleTimeString();
  }
}

class SwipeHandler {
  private swipeCoord?: [number, number];
  private swipeTime?: number;

  constructor(private readonly direction: Direction) {}

  swipe(e: TouchEvent, when: string): number {
    const coord: [number, number] = [
      e.changedTouches[0].clientX,
      e.changedTouches[0].clientY,
    ];
    const time = new Date().getTime();

    if (when === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;

      return 0;
    } else if (when === 'end') {
      const direction = [
        coord[0] - this.swipeCoord[0],
        coord[1] - this.swipeCoord[1],
      ];
      const duration = time - this.swipeTime;

      if (
        duration < 1000 && //
        Math.abs(direction[0]) > 30 && // Long enough
        Math.abs(direction[0]) > Math.abs(direction[1] * 3)
      ) {
        // Horizontal enough

        const isRtl = this.direction === 'rtl';
        const swipeLeft = direction[0] < 0;

        return isRtl ? (swipeLeft ? -1 : 1) : swipeLeft ? 1 : -1;
      }
    }

    return 0;
  }
}
